import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchLoginAttemptsQuery } from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const PAGE_SIZE = 50;

const FLOW_LABEL = { portal: 'Client portal', b2b: 'Entrepreneur' };

const ACTION_LABEL = {
  password:        'Sign-in',
  password_locked: 'Sign-in',
  request_code:    'Code emailed',
  verify_code:     'Code entered',
  reset_request:   'Forgot password',
  reset_complete:  'Password reset',
};

// What a successful row means, per attempt type.
const SUCCESS_LABEL = {
  password:       'Correct password',
  request_code:   'Code sent',
  verify_code:    'Code accepted',
  reset_request:  'Reset email sent',
  reset_complete: 'New password saved',
};

// Why it failed, in plain words. The person signing in never sees any of
// this - they always get the same generic message.
function failureLabel(row) {
  const d = row.detail || '';
  if (row.action === 'password_locked') return 'Locked out - too many wrong passwords';
  if (d === 'no account') return 'No account with this email';
  if (d === 'no eligible account') return 'No active account with this email - nothing sent';
  if (d.startsWith('rate_limited')) return 'Too many requests - nothing sent';
  if (d === 'email send failed') return 'Email failed to send';
  if (d === 'invalid') return 'Wrong or expired code';
  if (d === 'locked_out') return 'Too many wrong codes';
  if (!d) return 'Failed';
  return d.charAt(0).toUpperCase() + d.slice(1);
}

function ResultCell({ row }) {
  let text;
  if (row.success) {
    // The success row a completed reset writes to clear any lockout.
    text = row.action === 'password' && row.detail === 'reset'
      ? 'Lockout cleared by reset' : (SUCCESS_LABEL[row.action] || 'OK');
  } else {
    text = failureLabel(row);
  }
  return (
    <span style={{ color: row.success ? brand.green : brand.red, fontWeight: 600 }}>
      <i className={`bi ${row.success ? 'bi-check-circle' : 'bi-x-circle'} me-1`} />{text}
    </span>
  );
}
ResultCell.propTypes = { row: PropTypes.object.isRequired };

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
}) : '-');

// Admin-only view of client portal and Entrepreneur portal sign-in, code and
// password-reset attempts - with the email exactly as typed, so "the reset
// email never came" can be answered by spotting a mistyped address.
export default function SignInAttempts() {
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [flow, setFlow] = useState('');
  const [kind, setKind] = useState('');
  const [outcome, setOutcome] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setQ(qDraft.trim()), 300);
    return () => clearTimeout(id);
  }, [qDraft]);

  useEffect(() => { setPage(0); }, [q, flow, kind, outcome, dateFrom, dateTo]);

  const { data, isFetching, error, refetch } = useSearchLoginAttemptsQuery({
    q: q || undefined,
    flow: flow || undefined,
    kind: kind || undefined,
    outcome: outcome || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const hasFilters = Boolean(q || flow || kind || outcome || dateFrom || dateTo);
  const clearAll = () => {
    setQDraft(''); setFlow(''); setKind(''); setOutcome(''); setDateFrom(''); setDateTo('');
  };

  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  const selectStyle = {
    background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer',
  };
  const optStyle = { color: brand.charcoal };
  const thStyle = {
    padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem',
    textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap',
  };
  const tdStyle = { padding: '0.5rem', verticalAlign: 'middle' };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Header */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.5rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.65rem', flexShrink: 0 }}>
            <i className="bi bi-shield-lock" style={{ color: brand.goldenrod, fontSize: '1.5rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Support
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0 }}>Sign-in Attempts</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: '4px 0 0', fontSize: '0.85rem' }}>
              Every sign-in, verification code and &quot;Forgot your password?&quot; attempt on the
              client portal and the Entrepreneur portal, with the email exactly as it was typed
              and why a failed attempt failed.
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <i className="bi bi-search" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Attempts</h5>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {isFetching ? 'Loading…' : total === 0 ? '0 attempts' : `Showing ${rangeStart}-${rangeEnd} of ${total}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              type="search"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="Search email or IP…"
              aria-label="Search"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', width: '200px' }}
            />
            <select value={flow} onChange={(e) => setFlow(e.target.value)} aria-label="Portal" title="Portal" style={selectStyle}>
              <option value="" style={optStyle}>Both portals</option>
              <option value="portal" style={optStyle}>Client portal</option>
              <option value="b2b" style={optStyle}>Entrepreneur</option>
            </select>
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Attempt" title="Attempt" style={selectStyle}>
              <option value="" style={optStyle}>Any attempt</option>
              <option value="password" style={optStyle}>Sign-in (password)</option>
              <option value="code" style={optStyle}>Verification code</option>
              <option value="reset" style={optStyle}>Forgot password</option>
            </select>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="Result" title="Result" style={selectStyle}>
              <option value="" style={optStyle}>Any result</option>
              <option value="failure" style={optStyle}>Failed only</option>
              <option value="success" style={optStyle}>Succeeded only</option>
            </select>
            <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} title="From date" style={selectStyle} />
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} title="To date" style={selectStyle} />
            {hasFilters && (
              <button type="button" onClick={clearAll} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', color: 'white', cursor: 'pointer', fontWeight: 700 }}>
                <i className="bi bi-x-circle me-1" />Clear
              </button>
            )}
            <button type="button" onClick={refetch} title="Refresh" aria-label="Refresh" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', color: 'white', cursor: 'pointer' }}>
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
        </div>

        {error ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: brand.red }}>
            Could not load sign-in attempts. Please try again.
          </div>
        ) : isFetching && rows.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal }}>
            <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
            {hasFilters ? 'No attempts match these filters.' : 'No sign-in attempts recorded yet.'}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: '0.825rem' }}>
              <thead style={{ background: brand.softGray }}>
                <tr>
                  <th style={{ ...thStyle, paddingLeft: '0.75rem' }}>When</th>
                  <th style={thStyle}>Portal</th>
                  <th style={thStyle}>Email typed</th>
                  <th style={thStyle}>Attempt</th>
                  <th style={thStyle}>Result</th>
                  <th style={{ ...thStyle, paddingRight: '0.75rem' }}>IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...tdStyle, paddingLeft: '0.75rem', color: brand.charcoal, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created)}</td>
                    <td style={{ ...tdStyle, color: brand.tealBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                      {FLOW_LABEL[r.flow] || r.flow}
                    </td>
                    <td style={{ ...tdStyle, wordBreak: 'break-all' }}>
                      {r.typed_email ? (
                        // Click to see everything this address has tried.
                        <button type="button" onClick={() => setQDraft(r.typed_email)} title="Show only this email"
                          style={{ background: 'none', border: 'none', padding: 0, color: brand.deepBlue, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                          {r.typed_email}
                        </button>
                      ) : (
                        <span style={{ color: brand.charcoal }} title="Recorded before typed emails were kept">-</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: brand.charcoal, whiteSpace: 'nowrap' }}>{ACTION_LABEL[r.action] || r.action}</td>
                    <td style={tdStyle}><ResultCell row={r} /></td>
                    <td style={{ ...tdStyle, paddingRight: '0.75rem', color: brand.charcoal, whiteSpace: 'nowrap' }}>{r.request_ip || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1.25rem', borderTop: `1px solid ${brand.softGray}` }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{ background: 'transparent', border: `1px solid ${brand.charcoal}`, color: brand.charcoal, borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}
            >
              <i className="bi bi-chevron-left" /> Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => (rangeEnd < total ? p + 1 : p))}
              disabled={rangeEnd >= total}
              style={{ background: 'transparent', border: `1px solid ${brand.charcoal}`, color: brand.charcoal, borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: rangeEnd >= total ? 'not-allowed' : 'pointer', opacity: rangeEnd >= total ? 0.5 : 1 }}
            >
              Next <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
